/**
 * Where a place change in the search field lands (W10).
 *
 * THE CITY SWITCH THAT DID NOTHING (Muse audit 2026-10-20). On
 * /homes-for-sale/bend, picking Redmond wrote `?city=Redmond` onto the Bend
 * path. The slug route takes its place from the path (buildSearchFilters,
 * pathPlaceFilters) and never reads `?city=`, so the H1, the results and the
 * "Showing Bend only" chip all stayed Bend while the address bar said Redmond.
 *
 * A write that touches a geo key on a path that pins a place now leaves that
 * path: one city lands on its own city path (a preset segment rides along,
 * /homes-for-sale/bend/luxury becomes /homes-for-sale/redmond/luxury), any
 * other place set lands on the /homes-for-sale hub, which reads every geo key
 * from the query (the hub has no preset path, so a preset segment does not
 * survive that hop). Every other write keeps the current path.
 *
 * A NEW PATH IS A ROUTE CHANGE (QA on PR #759). The first cut handed the new
 * path to the same commit as a query write: history.pushState, then
 * router.push. The pushState moved the address bar and usePathname to
 * /homes-for-sale/redmond over the rendered Bend tree, and the push behind it
 * never replaced that tree, so the H1, title, chip, list and map stayed Bend
 * until a reload. `leavesPath` tells SearchFilters to send such a write to the
 * router alone.
 *
 * Pure. Tested in __tests__/filter-write-href.test.ts.
 */
import { GEO_SCOPE_KEYS } from '@/components/search/geo-scope'
import { isPresetSlug } from '@/lib/search-presets'
import { homesForSalePath } from '@/lib/slug'

const HUB = '/homes-for-sale'

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

/**
 * The href for a filter write. `params` is the query after the write;
 * `updates` names the keys the write changed.
 */
export function filterWriteHref(
  pathname: string | null | undefined,
  params: URLSearchParams,
  updates: Readonly<Record<string, string | undefined>>,
): string {
  const base = pathname || HUB
  const segments = base.startsWith(`${HUB}/`) ? base.slice(HUB.length + 1).split('/').filter(Boolean) : []
  const touchesGeo = GEO_SCOPE_KEYS.some((key) => key in updates)
  if (segments.length === 0 || !touchesGeo) return withQuery(base, params)

  const out = new URLSearchParams(params)
  const placed = GEO_SCOPE_KEYS.filter((key) => (out.get(key) ?? '').trim() !== '')
  const city = (out.get('city') ?? '').trim()
  if (placed.length === 1 && placed[0] === 'city' && !city.includes(',')) {
    const last = segments[segments.length - 1]!
    const preset = isPresetSlug(last) ? last.toLowerCase() : null
    out.delete('city')
    return withQuery(preset ? `${homesForSalePath(city)}/${preset}` : homesForSalePath(city), out)
  }
  return withQuery(HUB, out)
}

function trimSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path
}

/**
 * True when `href` lands on a different path from `pathname`: a route change,
 * not a query write. A null pathname reads as the hub, as in filterWriteHref.
 */
export function leavesPath(pathname: string | null | undefined, href: string): boolean {
  const target = new URL(href, 'https://ryan-realty.com').pathname
  return trimSlash(target) !== trimSlash(pathname || HUB)
}
